import { Suspense, useEffect, useState } from 'react';

import type { AccountAuthConfig } from './config';
import { ChunkErrorBoundary } from './ChunkErrorBoundary';
import { lazyNamedRoute } from './lazyRoute';
import { AccountAuthGateway } from '../features/auth/AccountAuthGateway';
import { isPublicPlatformPath } from '../features/platform/routes.js';

const PlatformApp = lazyNamedRoute(() => import('../features/platform/PlatformApp'), 'PlatformApp', 'platform');
const PublicPlatformPage = lazyNamedRoute(() => import('../features/public/PublicPlatformPage'), 'PublicPlatformPage', 'public-platform');

export interface AccountSessionState {
  status: string;
  user?: Record<string, unknown> | null;
  impersonation?: Record<string, unknown> | null;
  error?: unknown;
}

export interface AccountRuntime {
  getState(): AccountSessionState;
  subscribe(listener: (state: AccountSessionState) => void): () => void;
  restore(): Promise<AccountSessionState>;
  retryRestore(): Promise<AccountSessionState>;
  loginWithPassword(credentials: { email: string; password: string }): Promise<AccountSessionState>;
  requestRendallCode(input: { login: string }): Promise<unknown>;
  verifyRendallCode(input: { login: string; code: string }): Promise<AccountSessionState>;
  request(path: string, options?: Record<string, unknown>): Promise<unknown>;
  upload(path: string, body: FormData, onProgress: (progress: { loaded: number; total: number; percent: number | null }) => void): Promise<unknown>;
  logout(): Promise<unknown>;
  impersonate(userId: string): Promise<AccountSessionState>;
  stopImpersonation(): Promise<unknown>;
  handoff(): string;
}

export function AccountApplication({ config, runtime }: {
  config: AccountAuthConfig;
  runtime: AccountRuntime;
}) {
  const [path, setPath] = useState(() => window.location.pathname);
  const [session, setSession] = useState<AccountSessionState>(() => runtime.getState());
  const [protectedRestoreDone, setProtectedRestoreDone] = useState(false);

  useEffect(() => {
    const onLocation = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onLocation);
    return () => window.removeEventListener('popstate', onLocation);
  }, []);

  useEffect(() => runtime.subscribe(setSession), [runtime]);

  const publicPath = isPublicPlatformPath(path);
  const authPath = path === '/' || path === '/react-auth' || path.startsWith('/react-auth/');

  useEffect(() => {
    let active = true;
    if (publicPath || authPath) {
      setProtectedRestoreDone(true);
      return () => { active = false; };
    }
    setProtectedRestoreDone(false);
    void runtime.restore()
      .catch(() => undefined)
      .finally(() => { if (active) setProtectedRestoreDone(true); });
    return () => { active = false; };
  }, [authPath, publicPath, runtime]);

  if (publicPath) return <ChunkErrorBoundary resetKey={path}><Suspense fallback={<ApplicationChunkFallback />}><PublicPlatformPage path={path} runtime={runtime} /></Suspense></ChunkErrorBoundary>;
  if (authPath) return <AccountAuthGateway config={config} runtime={runtime} />;

  if (!protectedRestoreDone || session.status === 'restoring') {
    return null;
  }

  if (session.status !== 'authenticated' || !session.user) {
    return <AccountAuthGateway config={config} runtime={runtime} />;
  }

  return <ChunkErrorBoundary resetKey={path} homePath="/conferencing/meetings/"><Suspense fallback={<ApplicationChunkFallback />}><PlatformApp runtime={runtime} session={session} /></Suspense></ChunkErrorBoundary>;
}

function ApplicationChunkFallback() {
  return <div className="application-chunk-fallback" role="status" aria-live="polite"><span className="visually-hidden">Загружаем раздел…</span></div>;
}
